import type { TplCost, TplMonth } from "./api";

export const COST_TYPES = [
  { costType: "pick_pack", label: "Pick and Pack", group: "order_fulfilment", groupLabel: "Order fulfilment" },
  { costType: "packaging", label: "Packaging", group: "order_fulfilment", groupLabel: "Order fulfilment" },
  { costType: "courier", label: "Courier", group: "order_fulfilment", groupLabel: "Order fulfilment" },
  { costType: "amendments", label: "Order amendments", group: "order_fulfilment", groupLabel: "Order fulfilment" },
  { costType: "fulfilment_unsplit", label: "Fulfilment (not itemised)", group: "order_fulfilment", groupLabel: "Order fulfilment" },
  { costType: "amazon_reshipping", label: "Amazon inbound", group: "amazon_reshipping", groupLabel: "Amazon inbound" },
  { costType: "goods_in", label: "Goods in", group: "other_fees", groupLabel: "Other fees" },
  { costType: "storage", label: "Storage", group: "other_fees", groupLabel: "Other fees" },
  { costType: "subscription", label: "Subscription", group: "other_fees", groupLabel: "Other fees" },
  { costType: "other_labour", label: "Other labour", group: "other_fees", groupLabel: "Other fees" },
  { costType: "other", label: "Other", group: "other_fees", groupLabel: "Other fees" },
  { costType: "other_not_itemised", label: "Other fees (not itemised)", group: "other_fees", groupLabel: "Other fees" },
  { costType: "stock_freight", label: "Stock freight", group: "freight_duty", groupLabel: "Freight and duty" },
  { costType: "duty_customs", label: "Duty and customs", group: "freight_duty", groupLabel: "Freight and duty" },
  { costType: "excluded", label: "Deposits and prepayments", group: "excluded", groupLabel: "Excluded" },
] as const;

export type TplValueMap = Record<string, number | null>;
export type TplRow = {
  costType: string; label: string; amount: TplValueMap; perParcel: TplValueMap; perUnit: TplValueMap;
};
export type TplBlock = {
  group: string; label: string; rows: TplRow[]; subtotalAmount: TplValueMap; subtotalPerParcel: TplValueMap;
  driver: Record<string, string | null>; driverCount: TplValueMap;
};

const numberOrNull = (value: string | null | undefined): number | null => value == null || !Number.isFinite(Number(value)) ? null : Number(value);
const sumOrNull = (values: Array<number | null>): number | null => {
  const present = values.filter((value): value is number => value !== null);
  return present.length ? present.reduce((total, value) => total + value, 0) : null;
};
const ratio = (amount: number | null, count: number | null): number | null => amount === null || count === null || count === 0 ? null : amount / count;

function matchingCosts(month: TplMonth, group: string): TplCost[] {
  return month.costs.filter((cost) => cost.group === group && cost.cost_type !== "excluded");
}

// The engine owns row and group order. The browser only takes the union in the
// order first served, retaining zero and negative rows. Every subtotal uses the
// block driver served for that month; disagreement is a contract failure, not
// an invitation to silently use all parcels.
export function tplBlocks(months: TplMonth[]): TplBlock[] {
  const blocks: TplBlock[] = [];
  for (const month of months) {
    for (const cost of month.costs) {
      if (cost.cost_type === "excluded") continue;
      let block = blocks.find((candidate) => candidate.group === cost.group);
      if (!block) {
        block = { group: cost.group, label: cost.group_label, rows: [], subtotalAmount: {}, subtotalPerParcel: {}, driver: {}, driverCount: {} };
        blocks.push(block);
      }
      if (!block.rows.some((row) => row.costType === cost.cost_type)) {
        block.rows.push({ costType: cost.cost_type, label: cost.label, amount: {}, perParcel: {}, perUnit: {} });
      }
    }
  }

  for (const block of blocks) {
    for (const month of months) {
      const costs = matchingCosts(month, block.group);
      const first = costs[0];
      const servedBlock = month.blocks?.find((candidate) => candidate.group === block.group);
      if (!month.blocks && first && costs.some((cost) => cost.driver !== first.driver || cost.driver_count !== first.driver_count)) {
        throw new Error(`3PL History block ${block.group} has conflicting drivers in ${month.month}`);
      }
      block.driver[month.month] = servedBlock?.driver ?? first?.driver ?? null;
      block.driverCount[month.month] = servedBlock?.driver_count ?? first?.driver_count ?? null;
      for (const row of block.rows) {
        const cost = costs.find((candidate) => candidate.cost_type === row.costType);
        row.amount[month.month] = numberOrNull(cost?.amount);
        row.perParcel[month.month] = numberOrNull(cost?.per_parcel);
        row.perUnit[month.month] = numberOrNull(cost?.per_unit);
      }
      const subtotal = servedBlock ? numberOrNull(servedBlock.amount) : sumOrNull(block.rows.map((row) => row.amount[month.month] ?? null));
      block.subtotalAmount[month.month] = subtotal;
      block.subtotalPerParcel[month.month] = servedBlock ? numberOrNull(servedBlock.per_parcel) : ratio(subtotal, first?.driver_count ?? null);
    }
  }
  return blocks;
}

export const tplTotal = (month: TplMonth, view: "total" | "per_parcel"): number | null => view === "per_parcel"
  ? numberOrNull(month.total_per_parcel)
  : month.blocks ? numberOrNull(month.total) : sumOrNull(month.costs.filter((cost) => cost.cost_type !== "excluded").map((cost) => numberOrNull(cost.amount)));

export type TplChartSeries = { key: string; name: string };
export function tplChartSeries(blocks: TplBlock[]): TplChartSeries[] {
  return blocks.map((block) => ({ key: `block:${block.group}`, name: block.label }));
}
export function tplChartRows(blocks: TplBlock[], months: TplMonth[], view: "total" | "per_parcel"): Array<Record<string, string | number | null>> {
  return months.map((month) => ({
    month: month.month,
    total: tplTotal(month, view),
    ...Object.fromEntries(blocks.map((block) => [`block:${block.group}`, view === "total" ? block.subtotalAmount[month.month] ?? null : block.subtotalPerParcel[month.month] ?? null])),
  }));
}

const nextMonth = (month: string): string => {
  const [year, value] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year, value, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
};
export const monthWords = (month: string): string => {
  const [year, value] = month.split("-").map(Number);
  return new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(year, value - 1, 1)));
};
export type TplFlagNote = { code: string; words: string; ranges: string[]; text: string };
export function tplFlagNotes(months: TplMonth[]): TplFlagNote[] {
  const notes: Array<{ code: string; words: string; months: string[] }> = [];
  for (const month of months) for (const flag of month.flags) {
    let note = notes.find((candidate) => candidate.code === flag.code);
    if (!note) { note = { code: flag.code, words: flag.words, months: [] }; notes.push(note); }
    note.months.push(month.month);
  }
  return notes.map((note) => {
    const ranges: Array<[string, string]> = [];
    for (const month of note.months) {
      const current = ranges[ranges.length - 1];
      if (current && nextMonth(current[1]) === month) current[1] = month;
      else ranges.push([month, month]);
    }
    const words = ranges.map(([from, to]) => from === to ? monthWords(from) : `${monthWords(from)} to ${monthWords(to)}`);
    return { code: note.code, words: note.words, ranges: words, text: `${note.words} (${words.join(", ")})` };
  });
}

export type TplTableRow = { key: string; label: string; values: TplValueMap; perUnit?: TplValueMap; kind: "block" | "cost" | "total" | "excluded" | "parcel" | "units" };
export function tplTableRows(blocks: TplBlock[], months: TplMonth[], view: "total" | "per_parcel"): TplTableRow[] {
  const rows: TplTableRow[] = blocks.flatMap((block) => [
    { key: `block:${block.group}`, label: block.label, values: view === "total" ? block.subtotalAmount : block.subtotalPerParcel, kind: "block" as const },
    ...block.rows.map((row) => ({ key: `cost:${row.costType}`, label: row.label, values: view === "total" ? row.amount : row.perParcel, perUnit: row.costType === "storage" ? row.perUnit : undefined, kind: "cost" as const })),
  ]);
  rows.push({ key: "total", label: "Total", values: Object.fromEntries(months.map((month) => [month.month, tplTotal(month, view)])), kind: "total" });
  if (months.some((month) => numberOrNull(month.excluded) !== 0)) rows.push({ key: "excluded", label: "Excluded", values: Object.fromEntries(months.map((month) => [month.month, numberOrNull(month.excluded)])), kind: "excluded" });
  const parcelRows: Array<[string, string, keyof TplMonth["parcels"]]> = [
    ["parcels:shopify", "Shopify", "shopify"], ["parcels:fba", "Amazon inbound", "fba"],
    ["parcels:wholesale", "Wholesale", "wholesale"], ["parcels:other", "Other", "other"], ["parcels:total", "Total parcels", "total"],
  ];
  rows.push({ key: "parcels", label: "Parcels", values: {}, kind: "block" });
  rows.push(...parcelRows.map(([key, label, field]) => ({ key, label, values: Object.fromEntries(months.map((month) => [month.month, month.parcels[field]])), kind: "parcel" as const })));
  rows.push({ key: "units", label: "Units sold (Shopify)", values: Object.fromEntries(months.map((month) => [month.month, month.units])), kind: "units" });
  return rows;
}

const csvCell = (value: unknown): string => {
  const text = value == null ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};
export function tplHistoryCsv(blocks: TplBlock[], months: TplMonth[], view: "total" | "per_parcel"): string {
  const rows = tplTableRows(blocks, months, view);
  return `${["Row", ...months.map((month) => month.month)].join(",")}\r\n${rows.map((row) => {
    const displayed = months.map((month) => {
      const value = row.values[month.month];
      if (value === null || value === undefined) return "";
      if (row.kind === "parcel" || row.kind === "units") return value;
      return (Math.abs(value) < 0.005 ? 0 : value).toFixed(2);
    });
    return [row.label, ...displayed].map(csvCell).join(",");
  }).join("\r\n")}\r\n`;
}
